import { useEffect, useState } from "react";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "../../../components/atoms/button";

import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import {
  useAttemptQuestionMutation,
  useExecuteTestCaseMutation,
  useLazyFinishOngoingAssessmentQuery,
  useSaveCodeMutation,
  useSaveSubjectiveMutation,
  useStartQuestionMutation
} from "@assessment/store/slices/api/assessmentApiSlice";
import { encodeToBase64 } from "@lxp-app/utils/encodeImperva.ts";
import { exitFullScreen } from "../../../utils/global/fullScreenFunctions";

import GetHeroIcon from "../../../utils/getHeroIcons";
import { ProctoringFunctionsContext } from "../../../contexts/proctoringFunctionsContext";
import { useShowErrors } from "./toasterWrapper";
import { BrowserProctoringFunctionsContext } from "@assessment/contexts/browserProctoringFunctionContext";
import { extractPlainText, saveAnswers } from "./saveAnswers";
import orgBasedFeatureNameMapping from "@assessment/utils/orgBasedFeatureName";
import { InfoComponent } from "@lxp-app/components/atoms/infoComponent";

// interface props {
//   timeSpentFns: {
//     updateTimeSpentHandler: () => void;
//     updateTimeSpentOnSection: () => void;
//   } | null;
// }

const ConfirmSubmissionModal = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const showErrors = useShowErrors();

  const { scheduleId } = useParams();

  const [finishOngoingAssessment] = useLazyFinishOngoingAssessmentQuery();

  const [saveSubjectiveApi] = useSaveSubjectiveMutation();
  const [startQuestion] = useStartQuestionMutation();
  const [saveCodeAPi] = useSaveCodeMutation();
  const [executeTestCaseApi] = useExecuteTestCaseMutation();
  const [attemptQuestion] = useAttemptQuestionMutation();

  const [message, setMessage] = useState("");

  const context = useContext(ProctoringFunctionsContext);
  const browserContext = useContext(BrowserProctoringFunctionsContext);
  if (!context || !browserContext) {
    throw new Error("Context Error");
  }
  const { stopProctoring } = context;
  const { stopBrowserProctoring } = browserContext;

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);
  const feedbackData = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.socialInteractions
  );
  const feedbackAllowed = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.feedbackAllowed
  );
  const showMarks = useAppSelector(
    (store) => store.AssessmentSlice.preAssessmentData?.showResults
  );
  const assessmentProgress = useAppSelector(
    (state) => state.AssessmentSlice.assessmentProgress
  );

  const assessmentData = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData
  );
  const user = useAppSelector((state) => state.UserInfoSlice.profile);

  const latestCodingState = useAppSelector(
    (store) => store.AssessmentSlice.latestCodingState
  );

  const [disableFunc, setDisableFunc] = useState(false);

  const totalFlaggedQuestions = assessmentData?.ongoingSections.reduce(
    (acc, curr) => acc + curr.totalFlaggedQuestions,
    0
  );

  const view = useAppSelector(
    (state) => state.AssessmentSlice.assessmentActiveView
  );

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  // if (
  //   !activeQuestionMeta?.sectionId ||
  //   !activeQuestionMeta.questionId
  // )
  //   return;

  const ongoingSections = assessmentData?.ongoingSections;

  // if (!ongoingSections || ongoingSections.length === 0) return;

  const responses = useAppSelector(
    (store) => store.AssessmentSlice.candidateResponse
  );

  const responsesFib = useAppSelector(
    (store) => store.AssessmentSlice.candidateFibResponse
  );

  useEffect(() => {
    if (
      ongoingSections &&
      activeQuestionMeta &&
      activeQuestionMeta?.sectionId
    ) {
      setMessage("");

      const targetSection = ongoingSections.find(
        (section) => section.sectionId === activeQuestionMeta.sectionId
      );
      const currentQn = targetSection?.questions.filter(
        (el: any) => el.questionId == activeQuestionMeta?.questionId
      );

      const currentQnType = currentQn?.[0]?.type;
      if (currentQnType == "subjective") {
        const subType = currentQn?.[0]?.submissionType?.[0];
        const subjectiveCandidateRes: any = activeQuestionMeta?.questionId
          ? responses[activeQuestionMeta.questionId!]
          : undefined;

        if (
          subType == "text" &&
          subjectiveCandidateRes &&
          subjectiveCandidateRes?.value &&
          extractPlainText(subjectiveCandidateRes?.value)
            .trim()
            .split(/\s+/)
            .filter(Boolean).length < 40
        )
          setMessage(
            t("assessment.confirmModal.lowWordCountWarning", {
              questionTitle: currentQn?.[0]?.questiontitle || ""
            })
          );
        else if (
          subType == "video" &&
          subjectiveCandidateRes &&
          subjectiveCandidateRes?.[0]?.length > 0
        )
          setMessage(
            t("assessment.confirmModal.videoNotSubmittedWarning", {
              questionTitle: currentQn?.[0]?.questiontitle || ""
            })
          );
      }
    }
  }, [ongoingSections, activeQuestionMeta, activeQuestionMeta?.sectionId, t]);

  // const totalUnansweredQuestions = assessmentData?.ongoingSections.reduce(
  //   (acc, curr) =>
  //     acc + (curr.totalQuestionCount - curr.totalAttemptedQuestions),
  //   0
  // );

  const orgBasedFeatureName =
    orgBasedFeatureNameMapping[(user?.org as string) || "default"] ||
    orgBasedFeatureNameMapping["default"];

  const totalUnansweredQuestions = Object.keys(assessmentProgress).reduce(
    (acc, curr) => {
      const questions = assessmentProgress[curr]?.questions || {};
      const unanswered = Object.values(questions).filter(
        (q: any) => q && q.status !== "completed"
      ).length;
      return acc + unanswered;
    },
    0
  );

  const assessmentSubmitHandler = async () => {
    try {
      setDisableFunc(true);

      if (
        ongoingSections &&
        activeQuestionMeta &&
        activeQuestionMeta?.sectionId
      ) {
        const targetSection = ongoingSections.find(
          (section) => section.sectionId === activeQuestionMeta.sectionId
        );

        await saveAnswers(
          targetSection,
          activeQuestionMeta,
          responses,
          ongoingId,
          dispatch,
          saveSubjectiveApi,
          startQuestion,
          saveCodeAPi,
          attemptQuestion,
          responsesFib,
          true
        );
      }

      if (!scheduleId) {
        throw {
          status: "",
          title: "Schedule Id Not Found",
          err: {},
          description: ""
        };
      }
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      // if (view == "question") {
      //   await timeSpentFns?.updateTimeSpentHandler();
      //   await timeSpentFns?.updateTimeSpentOnSection();
      // }

      if (latestCodingState) {
        try {
          const saveRes: any = await saveCodeAPi({
            ongoingId,
            questionId: latestCodingState.questionId,
            quesVersion: latestCodingState.version,
            body: {
              code: encodeToBase64(latestCodingState.code),
              language: latestCodingState.language
            }
          });

          if (saveRes?.data?.data?.id) {
            await executeTestCaseApi({
              ongoingId,
              body: {
                questionId: latestCodingState.questionId,
                questionVersion: latestCodingState.version,
                savedCodeId: saveRes.data.data.id,
                hidden: true
              }
            });
          }
        } catch (err) {
          console.error("Save/execute before finish failed:", err);
        }
      }

      const res = await finishOngoingAssessment({
        ongoingId: ongoingId,
        scheduleId: scheduleId
      });

      if (res.isError) {
        throw {
          status: res.status,
          title: t("assessment.confirmModal.failedToFinish", {
            featureName: orgBasedFeatureName
          }),
          err: res.error,
          description: ""
        };
      }

      const obtainedMarks = res?.data?.data?.obtainedMarks;
      const totalMarks = res?.data?.data?.totalMarks;
      const showScore = showMarks.includes("score") ? true : false;
      const showPercentage = showMarks.includes("percentage") ? true : false;
      const resultStatus = res?.data?.data?.resultStatus;

      stopProctoring();
      stopBrowserProctoring();
      exitFullScreen(dispatch);

      navigate(`/assessment/feedback/${scheduleId}?ongoingId=${ongoingId}`, {
        state: {
          marksObtained: obtainedMarks,
          totalMarks: totalMarks,
          ongoingId: ongoingId,
          feedbackData: feedbackData,
          showScore: showScore,
          showPercentage: showPercentage,
          showMarks: showMarks,
          feedbackAllowed: feedbackAllowed,
          feedbackId: feedbackData[0]?.contentId,
          resultStatus: resultStatus
        }
      });
      setDisableFunc(false);
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setDisableFunc(false);
    }
  };

  return (
    <div className="w-full md:w-[600px]">
      <p className="mt-8 text-base font-normal leading-5 text-secondary/50">
        {t("assessment.confirmModal.description", {
          featureName: orgBasedFeatureName
        })}
      </p>

      {message?.length > 0 && (
        <div className="bg-[#FEFCE8] px-4 py-3 rounded border flex justify-between items-center gap-3 mt-3">
          <GetHeroIcon
            name="ExclamationTriangleIcon"
            className="w-5 h-5 text-[#854D0E]"
            variant="outline"
          />
          <span className="text-sm font-medium text-[#854D0E] flex-1">
            {message}
          </span>
        </div>
      )}
      {/* Disclaimer */}
      {(totalUnansweredQuestions && totalUnansweredQuestions > 0) ||
      (totalFlaggedQuestions && totalFlaggedQuestions > 0) ? (
        <div className="bg-[#FEFCE8] px-4 py-3 rounded border flex justify-between items-center gap-3 mt-3">
          <GetHeroIcon
            name="ExclamationCircleIcon"
            className="w-5 h-5 text-[#854D0E]"
            variant="outline"
          />
          <span className="text-sm font-medium text-[#854D0E] flex-1">
            {t("assessment.confirmModal.unansweredQuestions", {
              count: totalUnansweredQuestions
            })}{" "}
            {totalFlaggedQuestions && totalFlaggedQuestions > 0
              ? t("assessment.confirmModal.andFlaggedQuestions", {
                  count: totalFlaggedQuestions
                })
              : ""}
          </span>
          <button
            type="button"
            className="w-[76px] h-7 border border-[#854D0E] rounded-sm py-1 px-3 flex items-center"
            onClick={() => {
              dispatch(
                AssessmentSliceActions.mutateAssessmentView("dashboard")
              );
              dispatch(AssessmentSliceActions.mutateActiveQuestionMeta(null));
              dispatch(
                AssessmentSliceActions.mutateReviewIndicationStyling(true)
              );
              dispatch(AssessmentSliceActions.mutateExpandAll(true));
              setTimeout(() => {
                dispatch(
                  AssessmentSliceActions.mutateReviewIndicationStyling(false)
                );
              }, 5000);
              dispatch(AssessmentSliceActions.mutateShowSubmissionModal(false));
            }}
          >
            <span className="text-[#854D0E] text-[14px] font-medium">
              {t("common.review")}
            </span>
          </button>
        </div>
      ) : null}
      {/* Buttons */}
      <div className="flex items-center justify-end gap-3 mt-8">
        <Button
          buttonColor="secondary"
          variant="outlined"
          onClick={() => {
            dispatch(AssessmentSliceActions.mutateShowSubmissionModal(false));
          }}
          disabled={disableFunc}
        >
          {t("common.close")}
        </Button>
        <Button
          buttonColor="primary"
          variant="contained"
          onClick={assessmentSubmitHandler}
          disabled={disableFunc}
        >
          {t("common.submit")}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmSubmissionModal;
